import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Checkbox from "@ingka/checkbox";
import * as Sentry from "@sentry/react";
import {
  getSelectedStore,
  getStores,
  selectStore,
  setEvents,
  useAppContext,
  getStoreBySlug,
  setBreadcrumbs,
  setError,
  getStoreEvents,
  selectEvent,
} from "../../contextProviders/AppContext";
import StoreSelector from "../../components/StoreSelector";
import { getEvents } from "../../services/storeEventApi";
import { Breadcrumb, CSSEvent, Store, StoreMap } from "../../types/model";
import { Page } from "../../types/Seo";
import ConditionalRender from "../../components/ConditionalRender";
import Seo from "../../components/Seo/Seo";
import { EVENT_LIST, EVENT_STORE_LIST } from "../../components/Seo/enums";
import analytics from "../../analytics";
import useScrollToTop from "../../hooks/ScrollToTop";
import Focus from "../../components/Focus";
import NoStoreSelected from "./NoStoreSelected";
import StoreSelected from "./StoreSelected";

const StoreSelection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: var(--space-200);
  > div:first-child {
    margin: 0 0 var(--space-200) 0;
  }

  ${({ theme }) => `${theme.breakpoints.lg} {
    flex-direction: row;
    margin-bottom: var(--space-400);
    > div:first-child {
      margin: 0;
      margin-inline-end: var(--space-300);
    }
    .checkbox {
      margin-top: auto;
      margin-bottom: 0.75rem;
    }
  }`};
`;

const Title = styled.h1`
  margin-bottom: var(--space-200);
  ${({ theme }) => `${theme.breakpoints.lg} {
    margin-bottom: var(--space-250);
  }`};
`;

const Events = () => {
  const [includeDigialEvents, setIncludeDigitalEvents] = useState(true);
  const { useSelector, dispatch } = useAppContext();
  const { storeName } = useParams<{ storeName: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const stores: StoreMap = useSelector(getStores);
  const selectedStore: Store = useSelector(getSelectedStore);
  const events: Array<CSSEvent> = useSelector(
    getStoreEvents(includeDigialEvents)
  );
  const urlStore: Store = useSelector(getStoreBySlug(storeName));
  const [pageName, setPageName] = useState<Page>(EVENT_LIST);

  useScrollToTop();

  useEffect(() => {
    dispatch(selectEvent(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (urlStore) {
      dispatch(selectStore(urlStore.storeNo));
    } else {
      dispatch(selectStore(null));
    }
  }, [urlStore, dispatch]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const apiEvents = await getEvents();
        dispatch(setEvents(apiEvents));
      } catch (e: any) {
        dispatch(setError(e.code));
        Sentry.captureException(e);
      }
    };

    fetchEvents();
  }, [dispatch]);

  useEffect(() => {
    analytics.sendPageview({
      page_type: "events",
      local_store: selectedStore?.storeNo || "startPage",
    });
  }, [selectedStore?.storeNo]);

  useEffect(() => {
    const breadCrumbs: Breadcrumb[] = [];
    if (selectedStore) {
      breadCrumbs.push({ text: selectedStore.storeName });
      setPageName(EVENT_STORE_LIST);
    }
    dispatch(setBreadcrumbs(breadCrumbs));
  }, [dispatch, selectedStore]);

  const onSelectStore = (e: React.FormEvent<HTMLSelectElement>) => {
    const target = e.target as HTMLSelectElement;
    const store = stores[target.value];
    if (store) {
      history.push(`/${store.slug}/`);
    } else {
      history.push("/");
    }
  };

  const onChangeDigitalEvents = (e: React.FormEvent<HTMLInputElement>) => {
    setIncludeDigitalEvents((e.target as HTMLInputElement).checked);
  };

  const title = selectedStore
    ? t("event.whatsGoingOnIn", { storeName: selectedStore?.storeName })
    : t("event.allEventsInCountry");

  return (
    <Focus>
      <Seo page={pageName} canonicalPath={location.pathname} />
      <Title>{title}</Title>
      <StoreSelection>
        <StoreSelector onChange={onSelectStore} selectedStore={selectedStore} />
        <Checkbox
          id="digital-events"
          onChange={onChangeDigitalEvents}
          checked={includeDigialEvents}
          label={t("event.showDigitalEvents")}
          value="digital-events"
        />
      </StoreSelection>

      <ConditionalRender
        predicate={Boolean(selectedStore)}
        fallback={<NoStoreSelected />}
      >
        <StoreSelected events={events} selectedStore={selectedStore} />
      </ConditionalRender>
    </Focus>
  );
};

export default Events;
